import React, { useRef } from 'react';
import { Formik, getIn, Form, Field } from 'formik';
import {
  makeStyles,
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  Link,
  Tooltip,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { globalDateEntity } from '../../constants/applicationConstants';
import { stringConstants } from '../../constants/stringConstants';
import LocalStorageService from '../../services/localStorage';
import DisplayBreadcrumbs from '../../components/displayBreadcrumbs/displayBreadcrumbs';
import ClearIcon from '@material-ui/icons/Clear';
import AlertDialog from '../../common/AlertDialog';
import { PRODUCTS, useCommonAuth } from '../../submodule';

const localStorageService = LocalStorageService.getService();
// Custom Styles
const useStyles = makeStyles((theme) => ({
  brandingboxWrapper: {
    width: '700px',
    margin: '0px auto',
  },
  logoImage: {
    width: '100%',
  },
  colorPicker: {
    marginTop: '7px',
  },
}));

const timezones = [
  { abbr: 'ast', name: '(UTC-04:00) AST Atlantic Standard Time' },
  { abbr: 'est', name: '(UTC-05:00) EST Eastern Standard Time' },
  { abbr: 'cst', name: '(UTC-06:00) CST Central Standard Time' },
  { abbr: 'mst', name: '(UTC-07:00) MST Mountain Standard Time' },
  { abbr: 'pst', name: '(UTC-08:00) PST Pacific Standard Time' },
  { abbr: 'wst', name: '(UTC+08:00) WST Western Standard Time' },
];

const fontFamilies = [
  { abbr: '', name: 'None' },
  { abbr: 'Abel, sans-serif', name: 'Abel, sans-serif' },
  { abbr: 'Rubik, sans-serif', name: 'Rubik, sans-serif' },
  { abbr: 'Helvetica, sans-serif', name: 'Helvetica, sans-serif' },
  { abbr: 'Verdana, sans-serif', name: 'Verdana, sans-serif' },
  { abbr: 'Trebuchet MS, sans-serif', name: 'Trebuchet MS, sans-serif' },
  { abbr: 'Gill Sans, sans-serif', name: 'Gill Sans, sans-serif' },
  { abbr: 'Arial Narrow, sans-serif', name: 'Arial Narrow, sans-serif' },
];

const validationSchema = Yup.object().shape({
  preferences: Yup.object().shape({
    timezone: Yup.string().required(stringConstants.requiredValidationMsg),
    primaryColor: Yup.string()
      .required(stringConstants.requiredValidationMsg)
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Must be a valid hex color code (e.g. #RRGGBB)'),
    primaryFontColor: Yup.string()
      .required(stringConstants.requiredValidationMsg)
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Must be a valid hex color code (e.g. #RRGGBB)'),
    primaryFontFamily: Yup.string().required(
      stringConstants.requiredValidationMsg,
    ),
    custom_branding: Yup.string()
      .max(
        100,
        `Custom Branding Text ${stringConstants.CanNotMoreThan.replace(
          '{NUMBER}',
          100,
        )}`,
      )
      .required(stringConstants.requiredValidationMsg),
  }),
});

function Branding() {
  const classes = useStyles();
  const formikRef = useRef();
  const { hasSettingsMenuAccess } = useCommonAuth();
  const history = useHistory();
  const commonAction = useStoreActions((actions) => actions.common);
  const [configData, userRole] = useStoreState((state) => [state.customize.configData, state.login.userInfo]);
  const settingsAction = useStoreActions((actions) => actions.settings);
  const [confirmAlert, setConfirmAlert] = React.useState(false);
  const [companyLogoFileBase64, setCompanyLogoFileBase64] = React.useState('');
  const [companyLogoFile, setCompanyLogoFile] = React.useState({});
  const [formTempered, setFormTempered] = React.useState(false);
  const [initialFormData, setInitialFormData] = React.useState({
    "preferences": {
      "timezone": globalDateEntity.defaultTimezone,
      "primaryColor": globalDateEntity.primaryColor,
      "primaryFontColor": globalDateEntity.primaryFontColor,
      "primaryFontFamily": globalDateEntity.primaryFontFamily
    }
  });

  // Function to convert the uploaded file to base64
  const fileTOBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(setCompanyLogoFileBase64(reader.result));
      reader.onerror = (error) => reject(error);
    });
  
  function clearLogo() {

    if (initialFormData.preferences) {
      setInitialFormData({...initialFormData, preferences: {...initialFormData.preferences, company_logo: ''}});
    }

    setCompanyLogoFileBase64('');
    setFormTempered(true);
    //initialFormData.preferences?.company_logo
  }

  const InputGroup = ({ name, label, children, ...rest }) => (
    <Field name={name}>
      {({
        field, // { name, value, onChange, onBlur }
        form: { touched, errors },
      }) => (
        <TextField
          key={name}
          fullWidth
          size="small"
          label={label}
          name={name}
          error={Boolean(getIn(errors, name))}
          helperText={getIn(errors, name)}
          variant="outlined"
          {...rest}
          {...field}
        >
          {children}
        </TextField>
      )}
    </Field>
  );

  React.useEffect(() => {
    if (userRole.roles && !hasSettingsMenuAccess(PRODUCTS.MAINAPP, userRole?.roles?.sso_title)) {
      history.push('/');
    }
  }, [userRole?.roles]);

  React.useEffect(() => {
    if (configData.preferences?.primaryColor && configData.preferences?.primaryFontColor) {
      localStorageService.setColor(configData.preferences?.primaryColor);
      localStorageService.setFontColor(configData.preferences?.primaryFontColor);
      localStorageService.setFontFamily(configData.preferences?.primaryFontFamily);
    }
    setInitialFormData({...initialFormData, ...configData});    
  }, [configData]);

  const renderFontFamilyOptions = () =>
    fontFamilies.map((option) => (
      <MenuItem
        style={{ textTransform: 'capitalize' }}
        key={option.abbr}
        value={option.abbr}
      >
        {option.name}
      </MenuItem>
    ));

  //Timezone Options Render
  const renderTimezoneOptions = () =>
    timezones.map((option) => (
      <MenuItem
        style={{ textTransform: 'capitalize' }}
        key={option.abbr}
        value={option.abbr}
      >
        {option.name}
      </MenuItem>
    ));

  const confirmAlertDialogOpts = {
    title: `Branding`,
    description: "There are unsaved changes. Are you sure you want to exit?",
    btnOkText: "Yes",
    btnCancelText: "No",
    onClose: (selectedValue) => {      
      if (selectedValue) {
        formikRef.current?.resetForm()
        setConfirmAlert(false);
        history.push('/');
      }
      else {
        setConfirmAlert(false);
      }
    },
  };

  return (
    <Grid container direction="row">
      <Grid item md={12} xs={12}>            
        <DisplayBreadcrumbs items={[{ title: "Branding", link: "" }]} />
      </Grid>
      <Grid item md={12} style={{padding: '30px 20px 10px 20px'}}>
        <Box className={classes.brandingboxWrapper}>
          <Typography variant="h6" component="h6" style={{marginBottom: '32px'}}>
            Branding
          </Typography>
          <Formik
            initialValues={initialFormData}
            innerRef={formikRef}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                if (
                  companyLogoFile &&
                  companyLogoFile.size &&
                  companyLogoFile.size > globalDateEntity.brandingLogoSize
                ) {
                  errors.preferences.company_logo = stringConstants.brandingLogoSize;
                  return false;
                }
                if (
                  companyLogoFile &&
                  companyLogoFile.type &&
                  companyLogoFile.type != 'image/jpeg' &&
                  companyLogoFile.type != 'image/jpg' &&
                  companyLogoFile.type != 'image/png'
                ) {
                  errors.preferences.company_logo = stringConstants.brandingLogoType;
                  return false;
                }
                await settingsAction.updatePreferences({
                  ...values.preferences,
                  custom_branding: values.preferences && values.preferences.custom_branding ? values.preferences.custom_branding : globalDateEntity.siteHeaderTitle,
                  company_logo: companyLogoFileBase64,
                }).then(function (response) {
                  setCompanyLogoFileBase64('');
                  setCompanyLogoFile({});
                  commonAction.setSubmitSnackbar({
                    status: true,
                    messages: stringConstants.updateMsg,
                  });
                  
                  setFormTempered(false);

                  history.push('/');


                })
                .catch(function (error) {
                  if (
                    error.response &&
                    error.response.data &&
                    error.response.data.error
                  ) {
                    const outputErrors = {};
                    const errorlist = error.response.data.error;
                    for (const item in errorlist) {
                      outputErrors[Object.keys(errorlist[item])] =
                        errorlist[item][Object.keys(errorlist[item])];
                    }
                    setErrors(outputErrors);
                  }
                });                
                setSubmitting(false);
              } catch (err) {
                // console.log(err);
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, dirty, isSubmitting, handleBlur, setFieldValue }) => (
              <Box component={Form}>
                <Grid container spacing={2}>
                  <Grid item md={12}>
                    <InputGroup
                      name="preferences.custom_branding"
                      label="Header Text"
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <TextField
                      style={{ marginTop: '0px' }}
                      type="file"
                      size="small"
                      inputProps={{
                        accept:
                          'image/jpg, image/jpeg, image/png, image/JPG, image/JPEG, image/PNG',
                      }}
                      margin="normal"
                      fullWidth
                      label="Logo"
                      id="company_logo"
                      InputLabelProps={{ shrink: true }}
                      name="preferences.company_logo"
                      variant="outlined"
                      onChange={(event) => {
                        fileTOBase64(event.target.files[0]);
                        setCompanyLogoFile(event.target.files[0]);
                        setFieldValue(
                          'preferences.company_logo',
                          event.target.value,
                          true,
                        );
                      }}
                      error={Boolean(getIn(errors, "preferences.company_logo"))}
                      helperText={getIn(errors, "preferences.company_logo")}
                    ></TextField>
                  </Grid>
                  <Grid item md={3}>
                    {initialFormData && initialFormData.preferences?.company_logo && (
                      <Grid container>
                        <Grid item md={8}>
                          <a
                            href={`${initialFormData.preferences?.company_logo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              className={`${classes.logoImage}`}
                              src={initialFormData.preferences?.company_logo}
                            />
                          </a>
                        </Grid>
                        <Grid item md={4} style={{display:'flex', alignItems:'center'}}>
                          <Tooltip title="Delete Logo">
                            <Link onClick={clearLogo}><ClearIcon /></Link>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item sm={3}>
                    Logo Size: 3MB <br />
                    Logo Types: JPEG, JPG, PNG
                  </Grid>
                  <Grid item sm={12} style={{display:'none'}}>
                    <InputGroup
                      name="preferences.primaryFontFamily"
                      label="Font Family"
                      select
                    >
                      {renderFontFamilyOptions()}
                    </InputGroup>
                  </Grid>                  
                  <Grid item sm={12}>
                    <InputGroup
                      name="preferences.timezone"
                      label="Time Zone"
                      select
                    >
                      {renderTimezoneOptions()}
                    </InputGroup>
                  </Grid>                  
                  <Grid item sm={11}>
                    <InputGroup
                      name="preferences.primaryColor"
                      label="Primary Color"
                    ></InputGroup>
                  </Grid>
                  <Grid item sm={1} align="right">
                    <input
                      className={`${classes.colorPicker}`}
                      type="color"
                      value={values?.preferences?.primaryColor}
                      onChange={(e, v) => {
                        setFieldValue(
                          'preferences.primaryColor',
                          e.target.value,
                          true,
                        );
                        //setIsDirtyForm(true);
                      }}
                    />
                  </Grid>
                  <Grid item sm={11}>
                    <InputGroup
                      name="preferences.primaryFontColor"
                      label="Font Color"
                    ></InputGroup>
                  </Grid>
                  <Grid item sm={1} align="right">
                    <input
                      className={`${classes.colorPicker}`}
                      type="color"
                      value={values?.preferences?.primaryFontColor}
                      onChange={(e, v) => {
                        setFieldValue(
                          'preferences.primaryFontColor',
                          e.target.value,
                          true,
                        );
                        //setIsDirtyForm(true);
                      }}
                    />
                  </Grid>             
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  style={{marginTop: '20px', marginBottom: '20px'}}
                >
                  <Grid item>
                    <Button
                      disabled={!dirty && !formTempered}
                      onClick={() => { setConfirmAlert(true); }}
                      variant="contained"
                      color="secondary"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      autoFocus
                      variant="contained"
                      color="primary"
                      disabled={ (!dirty && !formTempered) || isSubmitting}
                      type="submit"
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Formik>
          <AlertDialog opts={confirmAlertDialogOpts}  open={confirmAlert}></AlertDialog>
        </Box>
      </Grid>
    </Grid>
  );
}

export default Branding;
